import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Typography from "../components/v2/Typography";
import { ChevronUp } from "lucide-react";
import ContactSection from "../atoms/Contact";
import Footer from "../atoms/Footer";

const TermsPage = () => {
    const [showScrollTop, setShowScrollTop] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <div className="legal-page">
                <div className="legal-container">
                    <div className="legal-header" style={{ paddingTop: '80px' }}>
                        <Typography variant="h1">Terms of Service</Typography>
                        <p className="last-updated">Last updated: January 2025</p>
                    </div>

                    <div className="legal-content">
                        <div className="table-of-contents">
                            <h3>Table of Contents</h3>
                            <ul>
                                <li><a href="#acceptance">1. Acceptance of Terms</a></li>
                                <li><a href="#services">2. Description of Services</a></li>
                                <li><a href="#registration">3. Registration and Account</a></li>
                                <li><a href="#use-conduct">4. Use of Services and Conduct</a></li>
                                <li><a href="#fees">5. Fees and Payment</a></li>
                                <li><a href="#intellectual-property">6. Intellectual Property</a></li>
                                <li><a href="#privacy">7. Privacy Policy</a></li>
                                <li><a href="#disclaimers">8. Disclaimers and Limitations</a></li>
                                <li><a href="#indemnification">9. Indemnification</a></li>
                                <li><a href="#termination">10. Termination</a></li>
                                <li><a href="#governing-law">11. Governing Law</a></li>
                                <li><a href="#contact">12. Contact Information</a></li>
                            </ul>
                        </div>

                        <div id="acceptance" className="legal-section">
                            <h2>1. Acceptance of Terms</h2>
                            <p>By accessing and using the NameConnect website and services ("Services"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
                            <p>These Terms of Service ("Terms") govern your use of NameConnect's domain brokerage, acquisition, and related services. We reserve the right to update these Terms at any time without prior notice.</p>
                        </div>

                        <div id="services" className="legal-section">
                            <h2>2. Description of Services</h2>
                            <p>NameConnect provides the following services:</p>
                            <ul>
                                <li>Domain name brokerage services</li>
                                <li>Domain acquisition assistance</li>
                                <li>Domain valuation and consulting</li>
                                <li>Portfolio management services</li>
                                <li>WHOIS lookup and domain research tools</li>
                                <li>Marketing services for domain sales</li>
                            </ul>
                            <p>Our services are designed to facilitate the buying and selling of domain names between parties. NameConnect acts as an intermediary and does not take ownership of domains unless specifically agreed upon in writing.</p>
                        </div>

                        <div id="registration" className="legal-section">
                            <h2>3. Registration and Account</h2>
                            <h3>3.1 Account Creation</h3>
                            <p>To use certain features of our Services, you may be required to create an account. You agree to:</p>
                            <ul>
                                <li>Provide accurate, current, and complete information</li>
                                <li>Maintain and promptly update your account information</li>
                                <li>Maintain the security of your password and account</li>
                                <li>Accept responsibility for all activities under your account</li>
                                <li>Notify us immediately of any unauthorized use</li>
                            </ul>
                            
                            <h3>3.2 Eligibility</h3>
                            <p>You must be at least 18 years old and capable of forming a binding contract to use our Services. By using our Services, you represent and warrant that you meet these requirements.</p>
                        </div>

                        <div id="use-conduct" className="legal-section">
                            <h2>4. Use of Services and Conduct</h2>
                            <h3>4.1 Acceptable Use</h3>
                            <p>You agree to use our Services only for lawful purposes and in accordance with these Terms. You agree not to:</p>
                            <ul>
                                <li>Violate any laws, regulations, or third-party rights</li>
                                <li>Engage in fraudulent or deceptive practices</li>
                                <li>Interfere with or disrupt our Services or servers</li>
                                <li>Attempt to gain unauthorized access to any portion of our Services</li>
                                <li>Use our Services to transmit malicious code</li>
                                <li>Impersonate any person or entity</li>
                                <li>Collect or harvest any information from our Services</li>
                            </ul>

                            <h3>4.2 Domain Transactions</h3>
                            <p>When engaging in domain transactions through our Services, you agree to:</p>
                            <ul>
                                <li>Provide accurate information about domains you own or wish to acquire</li>
                                <li>Honor all agreements made through our platform</li>
                                <li>Complete transactions in good faith</li>
                                <li>Use approved escrow services for payment processing</li>
                            </ul>
                        </div>

                        <div id="fees" className="legal-section">
                            <h2>5. Fees and Payment</h2>
                            <h3>5.1 Commission Structure</h3>
                            <p>Our fees are structured as follows:</p>
                            <ul>
                                <li><strong>Domain Sales:</strong> 15% commission on the final sale price with a minimum fee of $1,000</li>
                                <li><strong>Domain Acquisitions:</strong> Negotiated flat fee or percentage based on transaction value</li>
                                <li><strong>Consulting Services:</strong> Hourly or project-based fees as agreed</li>
                                <li><strong>Additional Services:</strong> As specified in separate agreements</li>
                            </ul>

                            <h3>5.2 Payment Terms</h3>
                            <p>All fees are due upon successful completion of the transaction unless otherwise agreed in writing. We reserve the right to modify our fee structure with 30 days' notice.</p>

                            <h3>5.3 Escrow Services</h3>
                            <p>All domain transactions must use an approved escrow service (such as Escrow.com) to ensure secure payment and transfer. Escrow fees are typically split between buyer and seller unless otherwise agreed.</p>
                        </div>

                        <div id="intellectual-property" className="legal-section">
                            <h2>6. Intellectual Property</h2>
                            <h3>6.1 Our Intellectual Property</h3>
                            <p>All content on our website, including text, graphics, logos, and software, is the property of NameConnect or its licensors and is protected by intellectual property laws.</p>

                            <h3>6.2 Your Content</h3>
                            <p>You retain ownership of any content you submit to our Services. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and display such content in connection with providing our Services.</p>

                            <h3>6.3 Feedback</h3>
                            <p>Any feedback, suggestions, or ideas you provide about our Services may be used by us without any obligation to compensate you.</p>
                        </div>

                        <div id="privacy" className="legal-section">
                            <h2>7. Privacy Policy</h2>
                            <p>Your use of our Services is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand our practices regarding your personal information.</p>
                        </div>

                        <div id="disclaimers" className="legal-section">
                            <h2>8. Disclaimers and Limitations</h2>
                            <h3>8.1 Service Disclaimer</h3>
                            <p>Our Services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that our Services will be uninterrupted, secure, or error-free.</p>

                            <h3>8.2 Domain Information</h3>
                            <p>While we strive to provide accurate domain information and valuations, we cannot guarantee the accuracy of all information. Domain values are estimates based on market conditions and are subject to change.</p>

                            <h3>8.3 Third-Party Services</h3>
                            <p>We are not responsible for the actions or inactions of third parties, including domain registrars, escrow services, or other service providers.</p>

                            <h3>8.4 Limitation of Liability</h3>
                            <p>To the maximum extent permitted by law, NameConnect shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our Services. Our total liability shall not exceed the fees paid by you in the twelve months preceding the claim.</p>
                        </div>

                        <div id="indemnification" className="legal-section">
                            <h2>9. Indemnification</h2>
                            <p>You agree to indemnify, defend, and hold harmless NameConnect, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney's fees) arising from:</p>
                            <ul>
                                <li>Your use of our Services</li>
                                <li>Your violation of these Terms</li>
                                <li>Your violation of any rights of another party</li>
                                <li>Any content you submit to our Services</li>
                            </ul>
                        </div>

                        <div id="termination" className="legal-section">
                            <h2>10. Termination</h2>
                            <h3>10.1 Termination by You</h3>
                            <p>You may terminate your account at any time by contacting us. Termination does not relieve you of any obligations incurred prior to termination.</p>

                            <h3>10.2 Termination by Us</h3>
                            <p>We reserve the right to suspend or terminate your account and access to our Services at any time, with or without cause, including for violation of these Terms.</p>

                            <h3>10.3 Effect of Termination</h3>
                            <p>Upon termination, your right to use our Services will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive.</p>
                        </div>

                        <div id="governing-law" className="legal-section">
                            <h2>11. Governing Law</h2>
                            <h3>11.1 Applicable Law</h3>
                            <p>These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which NameConnect is registered, without regard to its conflict of law provisions.</p>

                            <h3>11.2 Dispute Resolution</h3>
                            <p>Any disputes arising out of or relating to these Terms or our Services shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.</p>

                            <h3>11.3 Entire Agreement</h3>
                            <p>These Terms constitute the entire agreement between you and NameConnect regarding the use of our Services and supersede all prior agreements and understandings.</p>
                        </div>

                        <div id="contact" className="legal-section">
                            <h2>12. Contact Information</h2>
                            <div className="contact-info">
                                <p>If you have any questions about these Terms of Service, please contact us at:</p>
                                <p><strong>NameConnect</strong></p>
                                <p>Email: <a href="mailto:info@nameconnect.com">info@nameconnect.com</a></p>
                                <p>Phone: +1 (941) 500-9689</p>
                                <p>Address: 555 Grove Street, Herndon VA, 20170</p>
                            </div>
                        </div>
                    </div>
                </div>

                <button 
                    className={`scroll-to-top ${showScrollTop ? 'visible' : ''}`}
                    onClick={scrollToTop}
                    aria-label="Scroll to top"
                >
                    <ChevronUp size={24} />
                </button>
            </div>

            <ContactSection />
        </>
    );
};

export default TermsPage;